/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Button } from '@marriott/mi-ui-library';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import {
  AEMInternetBarNewAgeProps,
  AEMInternetBarProps,
  InternetBarExperienceProps,
  InternetBarStateTypes,
} from '../../InternetBar.types';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { serviceStore, useServiceStore } from '../../../../modules/store/serviceStore';
import { INTERNET_BAR_INITIAL_STATE, NEW_AGE_UX } from '../../utils/constant';
import { globalDivisionList, internetBarIcons } from '../../../../modules/utils/constants/constants';
import { connectBtnTrigger, getEnableSkipEnrollmentVerbiage, getEnrolledUserFlag } from '../../utils/handler';
import { isMobile } from '../../internetBar.helper';
import { updateDataLayerProperties, sanitize } from '../../../../modules/utils/common';
import { useSession } from '@marriott/mi-headless-utils';

export const InternetBarConservativeApproachExperience: FC<InternetBarExperienceProps> = (
  pageProps: InternetBarExperienceProps
) => {
  const [loyaltyConnectScenarioTextLabel, setLoyaltyConnectScenarioTextLabel] = useState<string>(
    pageProps?.loyaltyConnectScenarioText
  );

  const [loyaltyConnectScenarioDescriptionLabel, setLoyaltyConnectScenarioDescriptionLabel] = useState<string>(
    pageProps?.loyaltyConnectScenarioDescription
  );

  const { preProcessorResponse } = useContext(PageParamsContext);
  const termsAndConditionsRef = useRef<HTMLParagraphElement>(null);

  const { state = '', userState, propertyInfo } = preProcessorResponse || {};
  const globalDivision = propertyInfo?.data?.basicInformation?.globalDivision;

  const {
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
  } = pageProps;

  const { userExperience = '' } = pageProps?.internetPricingData?.data ?? {};

  const {
    enrollmentSourceCodeMapping,
    enableLoyalty,
    enableLoyaltyPostConnect,
    enableLoyaltyConference,
    enableLoyaltyConferencePostConnect,
    isLoyaltyEnabledForConference,
  } = preProcessorResponse || {};

  const enableSkipEnrollmentVerbiage = getEnableSkipEnrollmentVerbiage(
    enableLoyalty ||
      enableLoyaltyPostConnect ||
      ((enableLoyaltyConference || enableLoyaltyConferencePostConnect) && isLoyaltyEnabledForConference)
      ? enrollmentSourceCodeMapping?.loyalty
      : enrollmentSourceCodeMapping?.nonloyalty,
    globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );

  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;
  const { datalayer, isACDLenabled } = useSession();

  const serviceData = useServiceStore((state: serviceStore) => state.serviceData);

  const [isNoLoginInterfaceModal, setIsNoLoginInterfaceModal] = useState<boolean>(
    currentStateMapping?.noLoginInterfaceModal ?? false
  );

  const [showIcon] = useState<boolean>(
    internetBarIcons && internetBarIcons?.[state] ? internetBarIcons?.[state]?.showIcon : false
  );

  const conservativeWithIncentiveTermsAndConditionsText = sanitize(
    (pageProps && pageProps?.['conservativeWithIncentiveTnC' as keyof AEMInternetBarNewAgeProps]?.toString()) ?? ''
  );

  const termsAndConditionsText = sanitize(
    (
      currentStateMapping?.termsAndConditions &&
      pageProps?.[currentStateMapping?.termsAndConditions as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  const offersTermsConditionText = sanitize(
    (pageProps && pageProps?.['offersTnCDescription' as keyof AEMInternetBarNewAgeProps]?.toString()) ?? ''
  );

  const [loyaltyJoinNow, setLoyaltyJoinNow] = useState<string>(pageProps?.joinButtonLink);
  const [isLoyaltyConnectNowBtnLoading, setLoyaltyConnectNowBtnLoading] = useState<boolean>(false);
  const [showOffersTermsCondition, setShowOffersTermsCondition] = useState<boolean>(false);
  const [joinAndConnectButtonClickTrack, setJoinAndConnectButtonClickTrack] = useState<string>('');
  const [connectNowClickTrack, setConnectNowClickTrack] = useState<string>('');

  useEffect(() => {
    if (userExperience && !isACDLenabled) {
      if (pageProps && pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
        const { enrollmentSourceCode = '', userExperience = '' } = pageProps?.internetPricingData?.data ?? {};
        const dataLayer = (window && window?.dataLayer) ?? {};
        updateDataLayerProperties(dataLayer, userExperience, enrollmentSourceCode);
      }
    }
  }, [pageProps?.internetPricingData?.data, isACDLenabled]);

  useEffect(() => {
    let joinNowClickTrack = '';
    let connectClickTrack = '';
    let propertyType = '';
    if (
      pageProps &&
      pageProps?.internetPricingData &&
      pageProps?.internetPricingData?.data &&
      pageProps?.internetPricingData?.data?.enrollmentSourceCode
    ) {
      const { uxlGlobalDivision, enrollmentSourceCode } = pageProps?.internetPricingData?.data ?? {};
      if (datalayer) {
        propertyType = datalayer?.['prop_brand_tier'] as string;
      }
      joinNowClickTrack = `InternetBar|${uxlGlobalDivision
        ?.toString()
        ?.replaceAll(' ', '')};${propertyType};${userState};${enrollmentSourceCode};JoinAndConnect|external`;
      connectClickTrack = `InternetBar|${uxlGlobalDivision
        ?.toString()
        ?.replaceAll(' ', '')};${propertyType};${userState};${enrollmentSourceCode};ConnectNow|external`;
    } else {
      joinNowClickTrack = `Internet Bar|JoinAndConnect|external`;
      connectClickTrack = `Internet Bar|ConnectNow|external`;
    }
    setJoinAndConnectButtonClickTrack(joinNowClickTrack);
    setConnectNowClickTrack(connectClickTrack);
  }, [pageProps, pageProps?.internetPricingData, userState, datalayer]);

  useEffect(() => {
    if (pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
      const { enrollmentSourceCode } = pageProps?.internetPricingData?.data ?? {};
      const joinNowLink = pageProps?.handleLinks(enrollmentSourceCode);
      setLoyaltyJoinNow(joinNowLink);
    } else if (
      pageProps?.internetBarCurrentStateData &&
      pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
    ) {
      const joinNowLink = pageProps?.handleLinks(
        pageProps?.internetBarCurrentStateData?.['loyaltyEnrollmentSourceCode']
      );
      setLoyaltyJoinNow(joinNowLink);
    }
  }, [pageProps?.internetPricingData?.data, pageProps]);

  useEffect(() => {
    if (
      userExperience?.toLowerCase() === NEW_AGE_UX.conservativeWithIncentive &&
      termsAndConditionsRef &&
      termsAndConditionsRef?.current
    ) {
      const termsAndConditionsLinks = termsAndConditionsRef.current.getElementsByTagName('a');
      if (termsAndConditionsLinks.length > 1) {
        const offersTermsConditionLink = termsAndConditionsLinks[1];
        const handleClick = () => {
          setShowOffersTermsCondition(true);
          setTimeout(() => {
            pageProps?.resizeImage && pageProps?.resizeImage();
            if (isMobile()) {
              offersTermsConditionLink?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 0);
        };
        offersTermsConditionLink.addEventListener('click', handleClick);

        // Cleanup function to remove the event listener on component unmount
        return () => {
          offersTermsConditionLink.removeEventListener('click', handleClick);
        };
      }
    }
    return undefined;
  }, [userExperience, termsAndConditionsRef]);

  useEffect(() => {
    if (enableSkipEnrollmentVerbiage) {
      setLoyaltyConnectScenarioTextLabel(pageProps?.loyaltyConnectScenarioAPECText);
      setLoyaltyConnectScenarioDescriptionLabel(pageProps?.loyaltyConnectScenarioAPECDescription);
    }
  }, [enableSkipEnrollmentVerbiage]);

  useLayoutEffect(() => {
    pageProps?.resizeImage && pageProps?.resizeImage();
  });

  const handleBtnClick = (btnName: string) => {
    const { setCurrentStateHasModalOption, setModalType, setShowInternetBarModal, internetPricingData } = pageProps;
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const zoneValue = internetbarInitialValue['Z'] ?? '';
    const localRequirement = internetbarInitialValue['LR']?.toUpperCase() ?? '';
    const ppv5Enrollment =
      internetbarInitialValue['ppv5Enrollment'] ?? urlQueryParams?.['ppv5Enrollment']?.toLowerCase() === 'true';
    const loyaltyEnrollmentSourceCode = internetPricingData?.['data']?.enrollmentSourceCode
      ? internetPricingData?.['data']?.enrollmentSourceCode
      : internetbarInitialValue['loyaltyEnrollmentSourceCode'];
    const isUserEnrolled: boolean = getEnrolledUserFlag(loyaltyEnrollmentSourceCode);

    switch (btnName) {
      case 'connectnow':
        if (zoneValue?.toUpperCase() === 'GUEST') {
          const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData || {};
          if (
            globalDivision &&
            globalDivision === globalDivisionList?.APEC &&
            !localRequirement &&
            zoneMapping[Z ?? ''] === 'SF'
          ) {
            connectBtnTrigger(true, { ...internetbarInitialValue });
          } else {
            setShowInternetBarModal(true);
            setModalType('LOGIN');
            setCurrentStateHasModalOption(true);
          }
          if (localRequirement === 'STRICT_AO') {
            setModalType('ACCESS');
          } else if (isNoLoginInterfaceModal) {
            connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
          }
        } else if (zoneValue?.toUpperCase() === 'PUBLIC' || zoneValue?.toUpperCase() === 'LOBBY') {
          if (isUserEnrolled && ppv5Enrollment) {
            setShowInternetBarModal(true);
            setModalType('LOYALTY_WELCOME');
            setCurrentStateHasModalOption(true);
            setIsNoLoginInterfaceModal(false);
          } else {
            if (localRequirement && localRequirement !== 'TCTC') {
              if (localRequirement === 'AF_AO' || localRequirement === 'STRICT_AO') {
                setModalType('ACCESS');
                setShowInternetBarModal(true);
                setCurrentStateHasModalOption(true);
              } else {
                setModalType('LOGIN');
                setShowInternetBarModal(true);
                connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
              }
            } else {
              setModalType('');
              setShowInternetBarModal(false);
              setLoyaltyConnectNowBtnLoading(true);
              connectBtnTrigger(true, { ...internetbarInitialValue });
            }
          }
        }
        break;
    }
  };

  useEffect(() => {
    if (serviceData?.connectBtnClick) {
      handleBtnClick('connectnow');
    }
  }, [serviceData?.connectBtnClick]);

  return currentStateMapping && pageProps ? (
    <div className="internetbar-conservative-container">
      <div className={`internetbar-conservative-content px-3 px-md-4 py-4`}>
        <div className={'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center'}>
          {/* 1. Internet Bar Icon Configuration */}
          {showIcon && (
            <span className={`t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName} icon-xxl`}></span>
          )}
          {/* 2. Title */}
          <h3 data-testid="internet-bar-conservative_welcome-title" className={'col-12 t-subtitle-xl mt-2 mb-4'}>
            {pageProps?.title ?? ''}
          </h3>

          <div className={'loyalty-container'}>
            <div className={'d-flex flex-column flex-md-row'}>
              <div className="loyalty-container-card1 col-12 col-md-6 p-0 d-flex flex-column justify-content-between align-items-center pr-md-2 pr-lg-3">
                <div className={'d-flex flex-column'}>
                  <p
                    data-testid="internet-bar-conservative_card1-title"
                    className={'t-font-s loyalty-description-title mb-0'}
                  >
                    {pageProps?.loyaltyJoinnowScenarioText}
                  </p>
                  <p
                    data-testid="internet-bar-conservative_card1-description"
                    className={'t-font-s loyalty-description mb-0'}
                  >
                    {userExperience?.toLowerCase() === NEW_AGE_UX.conservativeWithIncentive ? (
                      pageProps?.conservativeBonusPointsDesc
                    ) : (
                      <span className={'mx-md-5 d-flex flex-column justify-content-between align-items-center'}>
                        {pageProps?.joinMBVMemberLabel}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  className={`m-button-primary mt-4 loyalty-buttons d-flex align-items-center justify-content-center ${
                    !pageProps?.enableLoyaltyJoinNowBtn ? 'disabled' : ''
                  }`}
                  isLink={true}
                  href={loyaltyJoinNow ?? pageProps?.joinButtonLink}
                  trackingProperties={pageProps?.trackingProperties}
                  target={pageProps?.joinButtonLinkInNewTab}
                  custom_click_track_value={joinAndConnectButtonClickTrack}
                  data-custom_click_track_value={joinAndConnectButtonClickTrack}
                  data-is-click-track={true}
                  isDisabled={pageProps?.isAuthorMode ? false : !pageProps?.enableLoyaltyJoinNowBtn}
                  ariaLabel={pageProps?.joinAndConnectButton}
                  testId="internet-bar-conservative_card1-btn"
                >
                  {!pageProps?.enableLoyaltyJoinNowBtn ? (
                    <div className="m-spinner-s" data-testid="activate-spinner"></div>
                  ) : (
                    <span>{pageProps?.joinAndConnectButton}</span>
                  )}
                </Button>
              </div>
              <div className="loyalty-container-card2 col-12 col-md-6 d-flex flex-column justify-content-between align-items-center px-0 pl-md-2 pl-lg-3">
                <div className={'d-flex flex-column justify-content-between align-items-center flex-1 pt-4 pt-md-0'}>
                  <p
                    data-testid="internet-bar-conservative_card2-title"
                    className={'t-font-s loyalty-description-title mb-0'}
                  >
                    {loyaltyConnectScenarioTextLabel}
                  </p>
                  <p
                    data-testid="internet-bar-conservative_card2-description"
                    className={'t-font-s loyalty-description mb-0'}
                  >
                    {loyaltyConnectScenarioDescriptionLabel}
                  </p>
                  <Button
                    className={`m-button-secondary loyalty-buttons mt-4 ${
                      isLoyaltyConnectNowBtnLoading ? 'disabled' : ''
                    }`}
                    isDisabled={isLoyaltyConnectNowBtnLoading}
                    trackingProperties={pageProps?.trackingProperties}
                    data-custom_click_track_value={connectNowClickTrack}
                    custom_click_track_value={connectNowClickTrack}
                    data-is-click-track={true}
                    ariaLabel={pageProps?.loyaltyConnectNowButton}
                    callback={() => {
                      handleBtnClick('connectnow');
                    }}
                    testId="internet-bar-conservative_card2-btn"
                  >
                    {isLoyaltyConnectNowBtnLoading ? (
                      <div className="m-spinner-s"></div>
                    ) : (
                      <span>{pageProps?.loyaltyConnectNowButton}</span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            {/* Separator */}
            <div className={`t-accent-color mt-4 internetbar-conservative-content__separator`}></div>

            {userExperience?.toLowerCase() === NEW_AGE_UX.conservativeWithIncentive
              ? pageProps?.conservativeWithIncentiveTnC && (
                  <p
                    ref={termsAndConditionsRef}
                    className={`mb-0 t-font-xs internetbar-conservative-content__footer-container__tnc-text`}
                    data-testid="internet-bar-terms-condition"
                    dangerouslySetInnerHTML={{
                      __html: conservativeWithIncentiveTermsAndConditionsText,
                    }}
                  ></p>
                )
              : pageProps?.termsAndConditions && (
                  <p
                    className={`mb-0 t-font-xs internetbar-conservative-content__footer-container__tnc-text`}
                    dangerouslySetInnerHTML={{
                      __html: termsAndConditionsText,
                    }}
                  ></p>
                )}

            {userExperience?.toLowerCase() === NEW_AGE_UX.conservativeWithIncentive && showOffersTermsCondition ? (
              <p
                className={`mb-0 t-font-xs internetbar-conservative-content__footer-container__tnc-text internetbar-conservative-content__footer-container__offers-tnc-text`}
                dangerouslySetInnerHTML={{
                  __html: offersTermsConditionText,
                }}
              ></p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  ) : null;
};
